import { useEffect, useMemo, useRef, useState } from "react";
import { IPanelScope } from "@/interfaces/IPanelScope";
import { FilterListMessage } from "./components/ListMessage";
import { AddNewItem, AddMenuItem } from "./components/AddNewListItem";
import { SearchInput } from "../SearchInput";
import { SelectAllItems } from "./components/SelectAllItems";
import { PanelSettings } from "@/types";
import { CollapseTreeButton, PtTreeItem } from "./components";
import { TreeService, SearchService, CacheService } from "@/services";
import { ITreeItem } from "@/interfaces/ITreeItem";
import {
  DEFAULT_FILTER_KEY,
  FILTERS_TAB_KEY,
  MIN_CRITERIA_LENGTH,
} from "@/constants";
import { ISearchResult } from "@/interfaces";
import { Loading } from "../Loading";
import { useFavoritesContext } from "@/contexts";
import { MuxInfoCircle, MuxToggle } from "@/Icons";
import { ToolTip } from "../ToolTip";
import styles from "./PtTree.module.scss";
import {
  FILTERS_SEARCHABLE_INPUT_TESTID,
  FILTERS_SEARCH_RESULT_TESTID,
  PT_LIST_TREE_TESTID,
} from "@/constants/test-ids";
import { LocalStorageKeys } from "@/enums";
import { FavoritePanel } from "@/components";
import { BareButton } from "../BareButton";
import { formatTranslation, getTranslation } from "@/utils";

interface IPtTreeProps {
  scope: IPanelScope;
  data: ITreeItem[];
  loading: boolean;
  error: string | null;
  settings: PanelSettings;
  criteria: string;
  contextMenu: any;
  selectedItem: any;
  hasEditPermissions: boolean;
  initOpenNodes: string[];
  setCriteria: (value: string) => void;
  notifyTreeNodeOpen?: (node: ITreeItem) => void;
  handleTreeItemClick: (node: ITreeItem) => void;
  handleAddItem?: () => void;
  addItemMenuItems?: AddMenuItem[];
  handleNodeRename: (id: string, value: string) => Promise<void>;
  handleSearch?:
    | ((criteria: string) => Promise<ISearchResult | undefined>)
    | undefined;
  handleStateFromUrl?: () => void;
}

export const PtTree = ({
  scope,
  data,
  loading,
  error,
  settings,
  contextMenu,
  criteria,
  selectedItem,
  initOpenNodes,
  setCriteria,
  handleTreeItemClick,
  handleAddItem,
  addItemMenuItems,
  hasEditPermissions,
  notifyTreeNodeOpen = () => {},
  handleNodeRename,
  handleSearch,
}: IPtTreeProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const infoIconRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<ITreeItem[]>(data);
  const [openNodes, setOpenNodes] = useState<string[]>([]);
  const [searchOpenNodes, setSearchOpenNodes] = useState<string[]>([]);
  const isInSearchMode = criteria.length > MIN_CRITERIA_LENGTH;
  const { renameFavorite, setActiveFavorite } = useFavoritesContext();
  const favoriteRef = useRef<HTMLUListElement>(null);
  const [numberOfResults, setNumberOfResults] = useState<number>(0);
  const [isFullResults, setIsFullResults] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState<boolean>(true);

  useEffect(() => {
    const isFilterTreeCollapsed = CacheService.getItemFromPersistCache(
      scope,
      LocalStorageKeys.FILTERS_TREE_COLLAPSE_STATE_KEY,
    );
    setIsOpen(!isFilterTreeCollapsed);

    if (selectedItem?.id === DEFAULT_FILTER_KEY) {
      handleSelectAll();
    }
  }, [selectedItem, scope.tab]);

  const isFavoriteTreeCollapsed = CacheService.getItemFromPersistCache(
    scope,
    LocalStorageKeys.FAVORITE_TREE_COLLAPSE_STATE_KEY,
  );

  const [isFavoriteOpen, setIsFavoriteOpen] = useState<boolean>(
    !isFavoriteTreeCollapsed,
  );

  useEffect(() => {
    setOpenNodes(initOpenNodes);
  }, [initOpenNodes]);

  useEffect(() => {
    if (!loading && !isInSearchMode && data?.length) {
      setVisibleItems([...data]);
    }
  }, [data, loading, isInSearchMode]);

  useEffect(() => {
    if (isInSearchMode) {
      (async () => {
        const lowercaseCriteria = criteria.toLowerCase();

        if (lowercaseCriteria.length < MIN_CRITERIA_LENGTH) {
          setVisibleItems([...data]);
        } else {
          await search(lowercaseCriteria);
        }
      })();
    } else {
      setSearchOpenNodes([]);
      setVisibleItems([...data]);
    }
  }, [criteria]);

  useEffect(() => {
    setNumberOfResults(
      SearchService.getNumberOfResults(visibleItems, criteria.toLowerCase()),
    );
  }, [visibleItems]);

  const search = useMemo(() => {
    return async (lowercaseCriteria: string): Promise<void> => {
      let searchResult: ISearchResult | undefined;
      if (handleSearch) {
        searchResult = await handleSearch(lowercaseCriteria);
      } else {
        searchResult = SearchService.search(data, lowercaseCriteria);
      }
      if (!searchResult) {
        return;
      }
      setIsFullResults(searchResult.fullResults);
      setVisibleItems([...searchResult.filteredTree]);
      setSearchOpenNodes([...searchResult.matchingIds]);
    };
  }, [handleSearch, data, criteria]);

  const onTreeItemClicked = (node: any) => {
    handleTreeItemClick(node);
  };

  const onCollapseTreeClicked = () => {
    TreeService.setOpenedNodes(scope, []);
    setOpenNodes([...[]]);
  };

  const handleTreeStateChange = (node: any, isOpen: boolean) => {
    if (isInSearchMode) {
      if (isOpen) {
        notifyTreeNodeOpen(node);
        if (!searchOpenNodes.includes(node.id)) {
          setSearchOpenNodes([...searchOpenNodes, node.id]);
        }
      } else {
        setSearchOpenNodes(searchOpenNodes.filter((id) => id !== node.id));
      }
    } else {
      if (isOpen) {
        notifyTreeNodeOpen(node);
        !openNodes.includes(node.id) && openNodes.push(node.id);
      } else {
        openNodes.splice(openNodes.indexOf(node.id), 1);
      }
      setOpenNodes([...openNodes]);
      TreeService.setOpenedNodes(scope, openNodes);
    }
  };

  const onRename = async (id: string, value: string): Promise<void> => {
    await handleNodeRename(id, value);
    await renameFavorite({ id, name: value });
  };

  const toggleTreeState = (type: string) => {
    let mixpanelcategory;
    if (type === "favorites") {
      mixpanelcategory = "favorites";
      CacheService.setItemToPersistCache(
        scope,
        LocalStorageKeys.FAVORITE_TREE_COLLAPSE_STATE_KEY,
        isFavoriteOpen,
      );
      setIsFavoriteOpen((prev) => !prev);
    } else {
      mixpanelcategory = scope.tab.toLowerCase();
      CacheService.setItemToPersistCache(
        scope,
        LocalStorageKeys.FILTERS_TREE_COLLAPSE_STATE_KEY,
        isOpen,
      );
      setIsOpen((prev) => !prev);
    }

    mixpanel.track(mixpanelcategory, {
      Action: "collapse-tree",
    });
  };

  const handleSelectAll = () => {
    setActiveFavorite(null);
    //@ts-expect-error in case of default only id __DEFAULT__ needed
    handleTreeItemClick({
      id: DEFAULT_FILTER_KEY,
    });
  };

  const testsetInstancesTooltip = getTranslation(
    "testset.filter-tree.instance-view-tooltip",
    "When a Test Set filter is applied in the Instance view,\nit returns instance results based on the criteria defined for the Test Sets",
  ).split("\n");

  return (
    <>
      <div
        className={styles.searchAddFilterContainer}
        data-testid={FILTERS_SEARCHABLE_INPUT_TESTID}
      >
        <SearchInput
          scope={scope}
          isActive={settings.enableSearch}
          criteria={criteria}
          handleCriteriaChange={setCriteria}
        />
        <AddNewItem
          isActive={settings.enableAddFilters}
          handleAddItem={handleAddItem}
          menuItems={addItemMenuItems}
        />
      </div>

      {settings.instanceViewMode && (
        <div
          className={styles.instanceViewIndicator}
          data-testid="instance-view-indicator"
        >
          {getTranslation(
            "testset.filter-tree.instance-view",
            "You are in Instance View Mode",
          )}
          <div
            className={styles.infoIcon}
            ref={infoIconRef}
            onMouseEnter={() => {
              if (infoIconRef.current) {
                const rect = infoIconRef.current.getBoundingClientRect();
                setTooltipPosition({
                  top: rect.top - 45, // Position it higher above the icon
                  left: rect.left - 87, // Shift it to the left for better visibility
                });
                setShowTooltip(true);
              }
            }}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <MuxInfoCircle />
            {showTooltip && (
              <ToolTip
                top={tooltipPosition.top}
                left={tooltipPosition.left}
                messages={testsetInstancesTooltip}
                type="INFO"
              />
            )}
          </div>
        </div>
      )}

      {isInSearchMode ? (
        <div
          data-testid={FILTERS_SEARCH_RESULT_TESTID}
          className={styles.searchResult}
        >
          {isFullResults
            ? getTranslation(
                "filters-tree.search.limited-number-of-results-shown",
                "First 200 results are shown",
              )
            : formatTranslation(
                "filters-tree.modal.select.results-found",
                `${numberOfResults} Results Found`,
                { n: numberOfResults },
              )}
        </div>
      ) : (
        <>
          <SelectAllItems
            settings={settings}
            scope={scope}
            contextMenu={contextMenu}
            onAllSelected={handleSelectAll}
            module={scope.module}
            selectedItem={selectedItem}
          />
        </>
      )}

      <FavoritePanel
        items={data}
        isOpen={isFavoriteOpen}
        selectedItem={selectedItem}
        handleTreeItemClick={handleTreeItemClick}
        setOpenState={() => toggleTreeState("favorites")}
        favoriteRef={favoriteRef}
        scope={scope}
        criteria={criteria}
        settings={settings}
      />

      <div className={styles.header}>
        <div className={styles.caption}>
          <BareButton
            handleClick={() => toggleTreeState("filters")}
            className={`${styles.barebtn} ${isOpen ? styles["collapse-node"] : styles["expand-node"]}`}
          >
            <MuxToggle />
          </BareButton>
          {getTranslation(
            `filters-tree.all.${scope.tab}`,
            `All ${scope.tab == FILTERS_TAB_KEY ? "Filters" : "Folders"}`,
          )}
        </div>
        <div>
          <CollapseTreeButton
            isDisabled={!openNodes?.length}
            handleCollapseTree={onCollapseTreeClicked}
          />
        </div>
      </div>
      <div className={styles.ContainerWithScroll}>
        {loading ? (
          <div className={styles.loaderContainer}>
            <Loading size="modal" />
          </div>
        ) : error ? (
          <FilterListMessage message={error} />
        ) : (
          <div
            data-testid={PT_LIST_TREE_TESTID}
            className={styles.listTree}
            style={{ display: isOpen ? "inline" : "none" }}
          >
            {visibleItems?.map((item) => (
              <PtTreeItem
                settings={settings}
                openNodes={isInSearchMode ? searchOpenNodes : openNodes}
                criteria={criteria}
                contextMenu={contextMenu}
                data={item}
                key={item.key}
                enableSettings={settings.enableSettings}
                scope={scope}
                level={0}
                selectedItemId={selectedItem?.id}
                hasEditPermissions={hasEditPermissions}
                notifyFilterChanged={onTreeItemClicked}
                handleTreeStateChange={handleTreeStateChange}
                handleNodeRename={onRename}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
